// Shared UI helper functions for agent display (Phase 1 internal)

import { AgentType } from '@/shared/types/common';

/** Map agent type to avatar background color (CSS variable) */
export function typeBgColor(type: AgentType): string {
  switch (type) {
    case AgentType.BACKEND:  return 'var(--color-element-red)';
    case AgentType.FRONTEND: return 'var(--color-element-blue)';
    case AgentType.TESTING:  return 'var(--color-element-green)';
    case AgentType.DEVOPS:   return 'var(--color-element-yellow)';
    case AgentType.HACKER:   return 'var(--color-element-purple)';
    default:                 return 'var(--color-bg-elevated)';
  }
}

/** Map agent type to display label */
export function typeLabel(type: AgentType): string {
  switch (type) {
    case AgentType.BACKEND:  return 'BACKEND';
    case AgentType.FRONTEND: return 'FRONTEND';
    case AgentType.TESTING:  return 'TESTING';
    case AgentType.DEVOPS:   return 'DEVOPS';
    case AgentType.HACKER:   return 'HACKER';
    default:                 return '';
  }
}

/** Map agent type to short abbreviation for badges */
export function typeAbbr(type: AgentType): string {
  switch (type) {
    case AgentType.BACKEND:  return 'BE';
    case AgentType.FRONTEND: return 'FE';
    case AgentType.TESTING:  return 'QA';
    case AgentType.DEVOPS:   return 'OP';
    case AgentType.HACKER:   return 'HK';
    default:                 return '??';
  }
}

/** Map agent type to Chinese label */
export function typeLabelCN(type: AgentType): string {
  switch (type) {
    case AgentType.BACKEND:  return '后端';
    case AgentType.FRONTEND: return '前端';
    case AgentType.TESTING:  return '测试';
    case AgentType.DEVOPS:   return '运维';
    case AgentType.HACKER:   return '黑客';
    default:                 return '';
  }
}

/** Map rarity number to label */
export function rarityLabel(rarity: number): string {
  switch (rarity) {
    case 1: return 'N';
    case 2: return 'R';
    case 3: return 'SR';
    case 4: return 'SSR';
    case 5: return 'UR';
    case 6: return 'UR+';
    default: return 'N';
  }
}

/** Portrait image path by agent config ID */
const PORTRAIT_MAP: Record<string, string> = {
  'backend-athena':     '/assets/characters/agents/backend_athena.webp',
  'backend-ghost':      '/assets/characters/agents/backend_ghost.webp',
  'backend-vulcan':     '/assets/characters/agents/backend_vulcan.webp',
  'backend-mercury':    '/assets/characters/agents/backend_mercury.webp',
  'backend-blade':      '/assets/characters/agents/backend_blade.webp',
  'frontend-aria':      '/assets/characters/agents/frontend_aria.webp',
  'frontend-fortress':  '/assets/characters/agents/frontend_fortress.webp',
  'frontend-luna':      '/assets/characters/agents/frontend_luna.webp',
  'frontend-vector':    '/assets/characters/agents/frontend_vector.webp',
  'frontend-nova':      '/assets/characters/agents/frontend_nova.webp',
  'testing-scout':      '/assets/characters/agents/testing_scout.webp',
  'testing-sentinel':   '/assets/characters/agents/testing_sentinel.webp',
  'testing-dash':       '/assets/characters/agents/testing_dash.webp',
  'testing-oracle':     '/assets/characters/agents/testing_oracle.webp',
  'testing-apex':       '/assets/characters/agents/testing_apex.webp',
  'devops-docker':      '/assets/characters/agents/devops_docker.webp',
  'devops-jenkins':     '/assets/characters/agents/devops_jenkins.webp',
  'devops-pulse':       '/assets/characters/agents/devops_pulse.webp',
  'devops-kubernetes':  '/assets/characters/agents/devops_kubernetes.webp',
  'devops-forge':       '/assets/characters/agents/devops_forge.webp',
  'hacker-zero':        '/assets/characters/agents/hacker_zero.webp',
  'hacker-cipher':      '/assets/characters/agents/hacker_cipher.webp',
  'hacker-phantom':     '/assets/characters/agents/hacker_phantom.webp',
  'hacker-glitch':      '/assets/characters/agents/hacker_glitch.webp',
  'hacker-neo':         '/assets/characters/agents/hacker_neo.webp',
};

export function agentPortrait(configId: string, generatedPortraitUrl?: string): string {
  if (generatedPortraitUrl) return generatedPortraitUrl;
  return PORTRAIT_MAP[configId] ?? '/assets/characters/agents/default.webp';
}

/** Map rarity to border color */
export function rarityColor(rarity: number): string {
  switch (rarity) {
    case 2: return '#8888aa';
    case 3: return 'var(--color-info)';
    case 4: return 'var(--color-element-purple)';
    case 5: return 'var(--color-element-yellow)';
    case 6: return 'var(--color-danger)';
    default: return 'var(--border-color)';
  }
}
